import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { RecipeCard } from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { SEO, generateWebsiteSchema, generateOrganizationSchema, injectJsonLd } from '../components/SEO';
import { ArrowRight, Heart, Leaf, Globe, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Home = () => {
  const { t, language } = useLanguage();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecipes();
    // Inject structured data for homepage
    injectJsonLd(generateWebsiteSchema(), 'website-schema');
    injectJsonLd(generateOrganizationSchema(), 'organization-schema');
    
    return () => {
      // Cleanup on unmount
      const ws = document.getElementById('website-schema');
      const os = document.getElementById('organization-schema');
      if (ws) ws.remove();
      if (os) os.remove();
    };
  }, []);

  const getSEOContent = () => {
    const content = {
      en: {
        title: 'love4vegan - Delicious Vegan Recipes | Plant-Based Cooking',
        description: 'Discover delicious vegan recipes for every occasion. From quick meals to French-inspired dishes, start your plant-based journey with love4vegan!'
      },
      fi: {
        title: 'love4vegan - Herkulliset Vegaanireseptit | Kasvisruoka',
        description: 'Löydä herkullisia vegaanireseptejä jokaiseen tilaisuuteen. Nopeista aterioista ranskalaisiin ruokiin - aloita vegaanimatkasi love4veganin kanssa!'
      },
      fr: {
        title: 'love4vegan - Délicieuses Recettes Véganes | Cuisine Végétalienne',
        description: 'Découvrez de délicieuses recettes véganes pour chaque occasion. Des repas rapides aux plats français, commencez votre voyage végétalien!'
      }
    };
    return content[language] || content.en;
  };

  const seoContent = getSEOContent();

  const fetchRecipes = async () => {
    try {
      // Fetch featured recipes first, then regular recipes
      const [featuredRes, allRes] = await Promise.all([
        axios.get(`${API}/recipes?featured=true`),
        axios.get(`${API}/recipes`)
      ]);
      
      // Combine: featured first, then others (excluding duplicates)
      const featuredIds = new Set(featuredRes.data.map(r => r.id));
      const otherRecipes = allRes.data.filter(r => !featuredIds.has(r.id));
      const combined = [...featuredRes.data, ...otherRecipes];
      
      setRecipes(combined.slice(0, 6));
    } catch (error) {
      console.error('Error fetching recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title={seoContent.title}
        description={seoContent.description}
        image="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1200"
        url="https://love4vegan.com/"
        type="website"
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1700150618387-3f46b6d2cf8e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt="Fresh organic vegetables - vegan cooking ingredients"
            loading="eager"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-2xl">
            <div className="animate-slideUp">
              <span className="font-accent text-secondary text-2xl md:text-3xl">
                🌱 love4vegan
              </span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mt-4 animate-slideUp animation-delay-100">
              {t('home.heroTitle')}
            </h1>
            
            <p className="text-lg md:text-xl text-foreground/70 mt-6 leading-relaxed animate-slideUp animation-delay-200">
              {t('home.heroSubtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-10 animate-slideUp animation-delay-300">
              <Link to="/recipes">
                <Button 
                  size="lg"
                  className="bg-primary text-primary-foreground rounded-full px-8 py-6 text-lg hover:bg-primary/90 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
                  data-testid="explore-recipes-btn"
                >
                  {t('home.exploreRecipes')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/why-vegan">
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-2 border-primary text-primary rounded-full px-8 py-6 text-lg hover:bg-primary hover:text-primary-foreground transition-all"
                  data-testid="learn-more-btn"
                >
                  {t('home.learnMore')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Vegan Section */}
      <section className="py-20 md:py-32 bg-card" data-testid="why-vegan-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-center mb-16">
            {t('home.whyVeganSection.title')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Animal Welfare */}
            <div className="group p-8 rounded-3xl bg-background hover:shadow-xl transition-all duration-300 hover:-translate-y-2" data-testid="animal-welfare-card">
              <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Heart className="h-8 w-8 text-secondary" />
              </div>
              <h3 className="text-2xl font-medium mb-3">
                {t('home.whyVeganSection.animalWelfare')}
              </h3>
              <p className="text-foreground/60 leading-relaxed">
                {t('home.whyVeganSection.animalWelfareDesc')}
              </p>
            </div>

            {/* Ecology */}
            <div className="group p-8 rounded-3xl bg-background hover:shadow-xl transition-all duration-300 hover:-translate-y-2" data-testid="ecology-card">
              <div className="w-16 h-16 rounded-2xl bg-muted/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Globe className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-2xl font-medium mb-3">
                {t('home.whyVeganSection.ecology')}
              </h3>
              <p className="text-foreground/60 leading-relaxed">
                {t('home.whyVeganSection.ecologyDesc')}
              </p>
            </div>

            {/* Health */}
            <div className="group p-8 rounded-3xl bg-background hover:shadow-xl transition-all duration-300 hover:-translate-y-2" data-testid="health-card">
              <div className="w-16 h-16 rounded-2xl bg-accent/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Sparkles className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="text-2xl font-medium mb-3">
                {t('home.whyVeganSection.health')}
              </h3>
              <p className="text-foreground/60 leading-relaxed">
                {t('home.whyVeganSection.healthDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Recipes Section */}
      <section className="py-20 md:py-32" data-testid="featured-recipes-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              {t('home.featuredRecipes')}
            </h2>
            <Link to="/recipes">
              <Button 
                variant="ghost" 
                className="text-primary hover:text-primary/80"
                data-testid="view-all-recipes-btn"
              >
                {t('home.exploreRecipes')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-card rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : recipes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recipes.map((recipe, index) => (
                <RecipeCard 
                  key={recipe.id} 
                  recipe={recipe} 
                  featured={index === 0}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Leaf className="h-16 w-16 text-muted mx-auto mb-4" />
              <p className="text-foreground/60 text-lg">
                {t('recipes.noRecipes')}
              </p>
              <p className="text-foreground/40 mt-2">
                Recipes will appear here once added via admin panel.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 md:py-32 bg-primary text-primary-foreground" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-center mb-16">
            {t('recipes.title')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Express */}
            <Link 
              to="/recipes?category=express" 
              className="group relative overflow-hidden rounded-3xl aspect-[4/3]"
              data-testid="category-express"
            >
              <img
                src="https://images.unsplash.com/photo-1683555974472-9cb9e2c35307?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Quick vegan meals - Express cooking under 30 minutes"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <h3 className="text-2xl font-semibold text-white">
                  {t('home.categories.express')}
                </h3>
              </div>
            </Link>

            {/* Guests */}
            <Link 
              to="/recipes?category=guests" 
              className="group relative overflow-hidden rounded-3xl aspect-[4/3]"
              data-testid="category-guests"
            >
              <img
                src="https://images.unsplash.com/photo-1741515044901-58696421d24a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Elegant vegan dishes for dinner parties and guests"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <h3 className="text-2xl font-semibold text-white">
                  {t('home.categories.guests')}
                </h3>
              </div>
            </Link>

            {/* Casual */}
            <Link 
              to="/recipes?category=casual" 
              className="group relative overflow-hidden rounded-3xl aspect-[4/3]"
              data-testid="category-casual"
            >
              <img
                src="https://images.unsplash.com/photo-1683556262029-56f31fbf75ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Everyday vegan comfort food for casual meals"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <h3 className="text-2xl font-semibold text-white">
                  {t('home.categories.casual')}
                </h3>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
